/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/layout/AdminLayout';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import {
  RefreshCw, AlertTriangle, Loader2, ClipboardList, ArrowRight, Beaker,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export default function FormulationVerification() {
  const { user } = useAuth();
  const [filter, setFilter] = useState('unverified');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const r = await axios.get(
        `${API}/formulation/inventory/unverified-subscribers?filter=${filter}`,
      );
      setItems(r.data.items || []);
    } catch (e) {
      setPageError(e?.response?.data?.detail || 'Could not load verification queue');
    } finally {
      setLoading(false);
    }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  if (!user) return null;

  return (
    <AdminLayout>
      <div
        data-testid="formulation-verification-page"
        className="px-6 py-6 max-w-5xl mx-auto"
      >
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-1">
            <Beaker className="w-6 h-6 text-cyan-300" />
            <h1 className="text-2xl font-semibold text-white">Physical Verification Queue</h1>
            <Badge className="bg-cyan-500/15 text-cyan-200 border-cyan-500/30 text-[10px]">
              F3
            </Badge>
          </div>
          <p className="text-sm text-white/50">
            Subscribers / phases with inventory rows awaiting physical count confirmation.
            Click a row to enter verification.
          </p>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <div className="rounded-lg border border-white/10 bg-slate-900/40 p-1 flex">
            <FilterButton active={filter === 'unverified'} onClick={() => setFilter('unverified')}>
              Unverified
            </FilterButton>
            <FilterButton active={filter === 'mismatch'} onClick={() => setFilter('mismatch')}>
              Mismatch
            </FilterButton>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            className="border-white/15 text-white/70 h-9 text-xs"
            data-testid="verification-refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>

        {loading && (
          <div className="rounded border border-white/10 bg-slate-900/40 p-8 text-center text-white/60">
            <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" /> Loading…
          </div>
        )}

        {pageError && !loading && (
          <div
            data-testid="verification-page-error"
            className="rounded border border-amber-500/30 bg-amber-500/5 p-6 text-center"
          >
            <AlertTriangle className="w-5 h-5 mx-auto text-amber-300 mb-2" />
            <div className="text-sm text-white/80">{pageError}</div>
            <Button size="sm" variant="outline" className="mt-3 border-white/15" onClick={load}>
              Retry
            </Button>
          </div>
        )}

        {!loading && !pageError && items.length === 0 && (
          <div
            data-testid="verification-empty-state"
            className="rounded-lg border border-white/10 bg-slate-900/40 p-8 text-center"
          >
            <ClipboardList className="w-6 h-6 mx-auto text-white/30 mb-2" />
            <div className="text-sm text-white/70">
              {filter === 'unverified'
                ? 'No phases currently have unverified inventory rows.'
                : 'No mismatches right now.'}
            </div>
          </div>
        )}

        {!loading && !pageError && items.length > 0 && (
          <div
            data-testid="verification-list"
            className="rounded-lg border border-white/10 bg-slate-900/40 overflow-hidden"
          >
            <table className="w-full text-xs">
              <thead className="bg-slate-950/60 border-b border-white/10 text-white/50">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Subscriber ID</th>
                  <th className="text-left px-3 py-2 font-medium">Phase ID</th>
                  <th className="text-right px-3 py-2 font-medium">
                    {filter === 'unverified' ? 'Unverified rows' : 'Mismatch rows'}
                  </th>
                  <th className="text-left px-3 py-2 font-medium">Last synced</th>
                  <th className="text-right px-3 py-2 font-medium">Open</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr
                    key={`${row.subscriber_id}:${row.phase_id}`}
                    data-testid={`verification-row-${row.subscriber_id}-${row.phase_id}`}
                    className="border-b border-white/5 hover:bg-white/[0.02]"
                  >
                    <td className="px-3 py-2 font-mono text-cyan-200">{row.subscriber_id}</td>
                    <td className="px-3 py-2 font-mono text-white/70">{row.phase_id}</td>
                    <td className="px-3 py-2 text-right text-white/80">{row.count}</td>
                    <td className="px-3 py-2 text-white/50">
                      {row.last_synced_at
                        ? row.last_synced_at.slice(0, 19).replace('T', ' ')
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to={`/admin/formulation/inventory/${row.subscriber_id}/${row.phase_id}`}
                        data-testid={`verification-open-${row.subscriber_id}-${row.phase_id}`}
                      >
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-cyan-500/30 text-cyan-200 hover:bg-cyan-500/10 h-7 text-[11px]"
                        >
                          Open <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function FilterButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded text-xs transition-colors ${
        active
          ? 'bg-cyan-500/20 text-cyan-200 border border-cyan-500/30'
          : 'text-white/60 hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}
